import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withGoogleMap } from 'react-google-maps';
import { fetchGoogleMapsStaticUrl } from 'bento-ordering/base/actions/googleMapsStaticUrl';
import { staticUrlQueryParams } from 'bento-ordering/base/constants/defaultMapProps';
/**
 * Note
 * Originally we were using withScriptJS from react-google-maps to load the
 * google javascript library to our WrappedComponent
 * ex: withScriptJS(withGoogleMap(WrappedComponent))
 *
 * This caused some errors when it tries to access the google api before
 * it fully loaded. Switching to loading the google map api
 * statically in index.html.
 *
 * This HOC is still used for the MapContainer because we need to pass in
 * loadingElement, containerElement, mapElement prop outside of the MapComponent
 */

const apiKey = process.env.GOOGLE_API_KEY;
const fullHeight = {
  height: '100%'
};
export const withGoogle = (WrappedComponent, isRequireMap) => {
  const WithGoogleComponent = isRequireMap
    ? withGoogleMap(WrappedComponent)
    : WrappedComponent;
  const FullHeight = <div style={fullHeight} />;
  class WithGoogle extends React.Component {
    static propTypes = {
      googleMapURL: PropTypes.string,
      loadingElement: PropTypes.node,
      containerElement: PropTypes.node,
      mapElement: PropTypes.node,
      dispatch: PropTypes.func.isRequired,
      locations: PropTypes.arrayOf(
        PropTypes.shape({
          lat: PropTypes.string,
          lng: PropTypes.string
        })
      ).isRequired,
      staticUrls: PropTypes.array.isRequired,
      isFetching: PropTypes.bool.isRequired
    };

    static defaultProps = {
      googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=3.exp&libraries=places`,
      loadingElement: <div />,
      containerElement: FullHeight,
      mapElement: FullHeight
    };

    componentDidMount() {
      const { staticUrls } = this.props;

      if (!staticUrls.length) {
        this.fetchGoogleMapsStaticUrl();
      }
    }

    fetchGoogleMapsStaticUrl() {
      const { locations, dispatch } = this.props;

      // this is an array containing one object with the latitude and longitude
      const { lat, lng } = locations[0];

      const queryParams = {
        lat,
        lng,
        ...staticUrlQueryParams
      };

      // a request is made to retrieve the Google maps static URLs for the desktop, tablet and mobile sizes
      dispatch(fetchGoogleMapsStaticUrl(queryParams));
    }

    /**
     * Return a hoc component that make use of google map
     */
    renderGoogleMapComponent = () => {
      const { props } = this;
      return (
        <WithGoogleComponent
          {...props}
          googleMapURL={props.googleMapURL}
          loadingElement={props.loadingElement}
          containerElement={props.containerElement}
          mapElement={props.mapElement}
        />
      );
    };

    /**
     * Return a hoc component that does not require google map
     */
    renderGoogleServiceComponent = () => {
      const { props } = this;
      return (
        <WithGoogleComponent
          staticUrls={props.staticUrls}
          isFetching={props.isFetching}
          {...props}
        />
      );
    };

    render() {
      return isRequireMap
        ? this.renderGoogleMapComponent()
        : this.renderGoogleServiceComponent();
    }
  }

  const mapStateToProps = state => {
    return {
      staticUrls: state.googleMapsStaticUrl.staticUrls,
      isFetching: state.googleMapsStaticUrl.isFetching
    };
  };

  return connect(mapStateToProps)(WithGoogle);
};
